import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type ClinicWhatsAppCloudConfigRow = {
  clinic_id: string;
  waba_id: string | null;
  is_enabled: boolean;
  phone_number_id: string | null;
  template_name: string | null;
  template_language: string;
  recipient_strategy: "phone_only" | "phone_first" | "bsuid_only";
  send_mode: "template" | "text";
  template_variable_mapping: Record<string, string> | null;
  updated_at: string | null;
};

export type ClinicWhatsAppCloudConfigInput = {
  isEnabled: boolean;
  phoneNumberId: string;
  templateName?: string | null;
  templateLanguage?: string;
  recipientStrategy: "phone_only" | "phone_first" | "bsuid_only";
  sendMode: "template" | "text";
  templateVariableMapping?: Record<string, string>;
};

export function useClinicWhatsAppCloudConfig(clinicId: string | null) {
  return useQuery({
    queryKey: ["clinic-whatsapp-cloud-config", clinicId],
    queryFn: async (): Promise<ClinicWhatsAppCloudConfigRow | null> => {
      if (!clinicId) return null;

      const { data, error } = await (supabase
        .from("clinic_meta_connections")
        .select("*")
        .eq("clinic_id", clinicId)
        .maybeSingle()) as { data: ClinicWhatsAppCloudConfigRow | null; error: Error | null };

      if (error) throw error;
      if (!data) return null;

      return data;
    },
    enabled: !!clinicId,
    staleTime: 1000 * 30,
  });
}

export function useUpsertClinicWhatsAppCloudConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { clinicId: string; config: ClinicWhatsAppCloudConfigInput }) => {
      const { clinicId, config } = input;
      const payload = {
        clinic_id: clinicId,
        is_enabled: config.isEnabled,
        phone_number_id: config.phoneNumberId || null,
        template_name: config.templateName || null,
        template_language: config.templateLanguage ?? "pt_BR",
        recipient_strategy: config.recipientStrategy,
        send_mode: config.sendMode,
        template_variable_mapping: config.templateVariableMapping ?? {},
      };

      const { error } = await (supabase
        .from("clinic_meta_connections")
        .upsert(payload, { onConflict: "clinic_id" })) as { error: Error | null };

      if (error) throw error;
      return true;
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ["clinic-whatsapp-cloud-config", variables.clinicId],
      });
    },
  });
}
